import { TTransport } from '@/types/models';
import { useTranslation } from '@/i18n';
import { TRACKING_CASES } from './constants';

export const DriverBox = ({
  selectedCase,
  transport_data,
  isMobile,
}: {
  selectedCase: string;
  transport_data: TTransport;
  isMobile?: boolean;
}) => {
  const { dateLocale, t } = useTranslation();

  const getDriverBoxContent = () => {
    switch (selectedCase) {
      case TRACKING_CASES.registered:
        return transport_data.pickup_date
          ? t('driverBox.expectedPickup', {
              date: new Date(transport_data.pickup_date).toLocaleString(
                dateLocale,
                { day: 'numeric', month: 'short' }
              ),
            })
          : t('driverBox.registered');
      case TRACKING_CASES.cancelled:
        return t('driverBox.cancelledBooking');
      case TRACKING_CASES.postponed:
        return t('driverBox.shipmentPostponed');
      case TRACKING_CASES.outForPickup:
      case TRACKING_CASES.intervalPickup:
        return `${t('driverBox.yourDriver')} ${
          transport_data.driver?.display_name
        }`;
      case TRACKING_CASES.intervalDelivery:
      case TRACKING_CASES.pickedUp:
      case TRACKING_CASES.outForDelivery:
        return null;
      case TRACKING_CASES.arrivalAtDepot:
      case TRACKING_CASES.departureFromDepot:
        return t('driverBox.internalTransit');
      case TRACKING_CASES.delivered:
        return t('driverBox.deliveredTo');
      case TRACKING_CASES.notFound:
        return t('driverBox.notFound');
    }
  };

  const renderPickedUpContent = () => {
    const driverName =
      transport_data.driver?.display_name || t('driverBox.driver');
    const nameParts = driverName.split(' ');
    const shortName =
      nameParts.length > 1
        ? `${nameParts[0]} ${nameParts[nameParts.length - 1].charAt(0)}.`
        : driverName;

    return (
      <div className='flex flex-col items-center justify-center text-center'>
        <span
          className={`${
            isMobile ? 'text-[10px]' : 'text-xs'
          } font-normal text-white/80`}
        >
          {t('driverBox.packageReceivedBy')}
        </span>
        <span
          className={`${
            isMobile ? 'text-sm' : 'text-lg'
          } font-extrabold text-white`}
        >
          {shortName}
        </span>
      </div>
    );
  };

  const renderOutForDeliveryContent = () => {
    const driverName =
      transport_data.driver?.display_name || t('driverBox.driver');
    return (
      <div className='flex flex-col items-center justify-center text-center'>
        <span
          className={`${
            isMobile ? 'text-[10px]' : 'text-sm'
          } font-normal text-white/80`}
        >
          {t('driverBox.yourDriver')}
        </span>
        <span
          className={`${
            isMobile ? 'text-sm' : 'text-base'
          } font-extrabold text-white`}
        >
          {driverName}
        </span>
      </div>
    );
  };

  const content = getDriverBoxContent();

  if (isMobile) {
    return (
      <span className='tracking-pill relative flex h-12 flex-1 cursor-pointer items-center rounded-full pl-4 pr-10'>
        <span className='min-w-0 flex-1 break-words text-center text-xs font-semibold text-white'>
          {selectedCase === TRACKING_CASES.pickedUp
            ? renderPickedUpContent()
            : selectedCase === TRACKING_CASES.outForDelivery ||
              selectedCase === TRACKING_CASES.intervalDelivery
            ? renderOutForDeliveryContent()
            : content}
        </span>
        <span className='tracking-pill-icon absolute -right-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-full'>
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='12' cy='8' r='4' stroke='#0c3a44' strokeWidth='2' />
            <path
              d='M4 20c0-4 3.6-7 8-7s8 3 8 7'
              stroke='#0c3a44'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
        </span>
      </span>
    );
  }

  return (
    <span className='tracking-pill relative flex h-[58px] w-[340px] cursor-pointer items-center justify-center rounded-full pl-5 pr-14'>
      {selectedCase === TRACKING_CASES.pickedUp ? (
        renderPickedUpContent()
      ) : selectedCase === TRACKING_CASES.outForDelivery ||
        selectedCase === TRACKING_CASES.intervalDelivery ? (
        renderOutForDeliveryContent()
      ) : (
        <span className='min-w-0 max-w-full whitespace-nowrap text-center text-[15px] font-semibold text-white'>
          {content}
        </span>
      )}
      <span className='tracking-pill-icon absolute -right-5 flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-full'>
        <svg
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <circle cx='12' cy='8' r='4' stroke='#0c3a44' strokeWidth='2' />
          <path
            d='M4 20c0-4 3.6-7 8-7s8 3 8 7'
            stroke='#0c3a44'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      </span>
    </span>
  );
};
